import React from 'react';
import { Alert, Flex, Skeleton, Tag, Typography, theme } from 'antd';

import { DEFAULT_CATEGORY_SCHEME, crawlerCategory, crawlerCategoryLabel } from '~features/crawler-filter';
import { AiCrawlerLineup, AiInsightsSectionHeading, useAiCrawlerLineup } from '../../shared';
import DiscoverabilityKPICard from './DiscoverabilityKPICard';
import {
  CRAWLER_ID_TO_CATEGORY,
} from '../DiscoverabilityCrawlers';
import KPIAbsoluteDelta from './KPIAbsoluteDelta';
import { formatErrorRatePercentage } from './ErrorRateFormatting';
import { getKPIAbsoluteComparison } from './KPIAbsoluteComparison';
import { useDiscoverabilitySearchParams } from '../hooks/useDiscoverabilitySearchParams';
import { useGetDiscoverabilityKpisQuery } from '../api/discoverabilityKpisApi';
import { USER_TIMEZONE } from '../userTimezone';

// Denominator of the "AI Crawlers detected" KPI: the number of providers the
// crawler filter selects (PRE-3118). The filter stores granular crawler ids in
// the URL, so fold them into provider categories before counting. No filter —
// or one whose ids all fail to resolve — means all tracked providers.
function selectedProviderCount(lineup: AiCrawlerLineup, crawlers: string[] | undefined): number {
  if (!crawlers || crawlers.length === 0) return lineup.categories.length;
  const categories = new Set(
    crawlers
      .map((id) => CRAWLER_ID_TO_CATEGORY[id])
      .filter((category): category is string => Boolean(category))
  );
  return categories.size > 0 ? categories.size : lineup.categories.length;
}

// Category labels, not provider names ("Perplexity AI", not "Perplexity"), so
// the tooltip names crawlers the way the card's "Missing:" tag does.
export function aiCrawlersDetectedTooltip(lineup: AiCrawlerLineup): string {
  const names = lineup.categories.map((category) => crawlerCategoryLabel(category));
  const listed = `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  return `AI Insights focuses on ${names.length} major AI crawlers: ${listed}. A crawler counts as detected if it requested at least one page of your site during the selected period.`;
}

// CustomTooltip renders its `message` as HTML (sanitized via DOMPurify, whose
// project config allows `ul`/`li`). The error-rate tooltip below is a bulleted
// list; this one is a single sentence (PRE-2821 — the card now shows a raw
// distinct-page count, not a percentage).
export const PAGES_CRAWLED_TOOLTIP =
  'Tracks the distinct pages AI Crawlers visited across your site during the selected period.';

export const ERROR_RATE_TOOLTIP =
  '<ul>' +
  '<li>Percentage of AI crawler requests that returned a server error (5xx). Redirects and access errors (3xx/4xx) are not included.</li>' +
  '<li>Red error rates = issues with rendering, server responses, or configuration.</li>' +
  '</ul>';

// Figma renders the headline number ("2/4", "56%", "10%") as Heading/2 —
// Inter Semi Bold 30/38. Inline style applies because `Typography.Title` adds
// margins and `Typography.Text strong` doesn't carry the heading-2 font size.
function MainValue({ children }: { children: React.ReactNode }) {
  const { token } = theme.useToken();
  return (
    <Typography.Text
      strong
      style={{
        fontSize: token.fontSizeHeading2,
        lineHeight: token.lineHeightHeading2,
        color: token.colorTextHeading,
      }}
    >
      {children}
    </Typography.Text>
  );
}

function WaitingForCrawlersBody() {
  const { token } = theme.useToken();
  return (
    <Typography.Text style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM }}>
      Waiting for AI Crawler requests
    </Typography.Text>
  );
}

type DiscoverabilityKPIsProps = {
  // Account has never been visited by an AI crawler (the KPI response's
  // `hasEverHadCrawlerActivity` is false) — computed by DiscoverabilityView so
  // the KPI cards and Pages to Review can never show different empty states
  // for the same account + range (PRE-3098).
  waitingForCrawlerRequests?: boolean;
};

function DiscoverabilityKPIs({ waitingForCrawlerRequests = false }: DiscoverabilityKPIsProps) {
  const { token } = theme.useToken();
  const { dateFrom, dateTo, crawlers, domain } = useDiscoverabilitySearchParams();
  const lineup = useAiCrawlerLineup();

  const { data, isLoading, isError } = useGetDiscoverabilityKpisQuery({
    dateFrom,
    dateTo,
    crawlers,
    domain,
    timezone: USER_TIMEZONE,
  });

  return (
    <Flex vertical gap={token.marginXS}>
      <AiInsightsSectionHeading title="KPIs" description="See how AI Crawlers interact with your site." />
      {isLoading && !data && <Skeleton active paragraph={{ rows: 3 }} />}
      {isError && (
        <Alert
          type="error"
          message="Could not load KPIs"
          showIcon
          style={{ margin: token.marginSM }}
        />
      )}
      {/* `wrap`: the cards are flex:1 but can't shrink below their content (the
          "Missing:" tag is the widest), so on a narrow container — the A4 export
          viewport above all — they wrap instead of overflowing and being clipped. */}
      {data && (
        <Flex gap={token.margin} align="stretch" wrap>
          <DiscoverabilityKPICard
            title="AI Crawlers detected"
            tooltip={aiCrawlersDetectedTooltip(lineup)}
          >
            {waitingForCrawlerRequests ? (
              <WaitingForCrawlersBody />
            ) : (
              <Flex align="center" gap={token.marginSM} wrap>
                <Flex align="baseline" gap={token.marginSM}>
                  <MainValue>{`${data.crawlersDetected.current}/${selectedProviderCount(lineup, crawlers)}`}</MainValue>
                  <KPIAbsoluteDelta
                    comparison={getKPIAbsoluteComparison(
                      data.crawlersDetected.current,
                      data.crawlersDetected.previous
                    )}
                    format="integer"
                  />
                </Flex>
                {data.crawlersDetected.missing.length > 0 && (
                  <Tag style={{ margin: 0 }}>
                    {`Missing: ${data.crawlersDetected.missing
                      .map((id) => crawlerCategoryLabel(crawlerCategory(id, DEFAULT_CATEGORY_SCHEME)))
                      .join(', ')}`}
                  </Tag>
                )}
              </Flex>
            )}
          </DiscoverabilityKPICard>
          <DiscoverabilityKPICard
            title="Pages Crawled (unique)"
            tooltip={PAGES_CRAWLED_TOOLTIP}
          >
            {waitingForCrawlerRequests ? (
              <WaitingForCrawlersBody />
            ) : (
              <Flex align="baseline" gap={token.marginSM}>
                <MainValue>{data.pageCoverage.current}</MainValue>
                <KPIAbsoluteDelta
                  comparison={getKPIAbsoluteComparison(
                    data.pageCoverage.current,
                    data.pageCoverage.previous
                  )}
                  format="integer"
                />
              </Flex>
            )}
          </DiscoverabilityKPICard>
          <DiscoverabilityKPICard title="Error rate" tooltip={ERROR_RATE_TOOLTIP}>
            {waitingForCrawlerRequests ? (
              <WaitingForCrawlersBody />
            ) : (
              <Flex align="baseline" gap={token.marginSM}>
                <MainValue>{formatErrorRatePercentage(data.errorRate.current)}</MainValue>
                <KPIAbsoluteDelta
                  comparison={getKPIAbsoluteComparison(
                    data.errorRate.current,
                    data.errorRate.previous
                  )}
                  format="errorRate"
                  inverted
                />
              </Flex>
            )}
          </DiscoverabilityKPICard>
        </Flex>
      )}
    </Flex>
  );
}

export default DiscoverabilityKPIs;
